package com.upcominds.expedite.features.achievements

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.ApiException
import com.upcominds.expedite.data.achievements.AchievementsRepository
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.dto.BadgeDTO
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class AchievementsUiState(
    val loading: Boolean = false,
    val error: String? = null,
    val communityId: String? = null,
    val achievements: List<BadgeDTO> = emptyList()
)

class AchievementsViewModel(
    private val achievementsRepository: AchievementsRepository
) : ViewModel() {
    private var loadJob: Job? = null
    private val _uiState = MutableStateFlow(AchievementsUiState())
    val uiState: StateFlow<AchievementsUiState> = _uiState.asStateFlow()

    fun load(communityId: String?) {
        loadJob?.cancel()
        if (communityId == null) {
            _uiState.value = AchievementsUiState()
            return
        }
        _uiState.value = AchievementsUiState(loading = true, communityId = communityId)
        loadJob = viewModelScope.launch {
            try {
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    achievements = achievementsRepository.getMyAchievements(communityId)
                )
            } catch (error: Exception) {
                if (error is CancellationException) throw error
                logMobileError("AchievementsViewModel.load", error)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = toUserMessage(error)
                )
            }
        }
    }

    private fun toUserMessage(error: Exception): String {
        val apiError = error as? ApiException
            ?: return error.message ?: "Something went wrong. Please try again."
        return when (apiError.statusCode) {
            401 -> "Your session has expired. Please sign in again."
            in 500..599 -> "Your achievements are unavailable right now. Please try again in a bit."
            else -> apiError.message.ifBlank { "Could not load your achievements." }
        }
    }
}
